/**
 * GuardrailsPanel — guardrail CRUD panel rendered as a sub-tab inside the
 * AI Agents page.  Mirrors the visual style of OrchestratorsPanel:
 *   - header row with title + "Create" button
 *   - card with toolbar (search, status filter)
 *   - table list with inline status toggle and row actions
 *   - right-side drawer for create / edit
 */
import { useCallback, useEffect, useState, type JSX } from 'react'
import {
  Shield,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  X,
  Wand2,
  Sparkles,
  Eye,
  Clock,
  RotateCcw,
} from 'lucide-react'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import RequirePermission from '../../auth/RequirePermission.js'
import { useConfirmDialog } from '../../ui/ConfirmDialog.js'
import Select from '../../ui/Select.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GuardrailVersion {
  version: number
  content: string
  notes: string | null
  createdBy: string | null
  createdAt: string
}

interface Guardrail {
  id: string
  tenantId: string
  slug: string
  name: string
  description: string | null
  content: string
  category: string | null
  isSystem: boolean
  isEnabled: boolean
  sortOrder: number
  currentVersion: number
  createdBy: string | null
  updatedBy: string | null
  createdAt: string
  updatedAt: string
  versions: GuardrailVersion[]
}

interface GuardrailFormData {
  slug: string
  name: string
  description: string
  content: string
  category: string
  isEnabled: boolean
  sortOrder: number
  notes: string
}

const EMPTY_FORM: GuardrailFormData = {
  slug: '',
  name: '',
  description: '',
  content: '',
  category: 'safety',
  isEnabled: true,
  sortOrder: 0,
  notes: '',
}

const CATEGORIES = ['safety', 'compliance', 'tone', 'scope', 'privacy', 'other'] as const
type _Category = (typeof CATEGORIES)[number]

const CATEGORY_BADGE: Record<string, string> = {
  safety: 'bg-red-50 text-red-700',
  compliance: 'bg-amber-50 text-amber-700',
  tone: 'bg-blue-50 text-blue-700',
  scope: 'bg-purple-50 text-purple-700',
  privacy: 'bg-orange-50 text-orange-700',
  other: 'bg-gray-100 text-gray-600',
}

type StatusFilter = 'all' | 'enabled' | 'disabled'

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function GuardrailsPanel(): JSX.Element {
  const toast = useToast()
  const { confirm, Dialog } = useConfirmDialog()
  const [guardrails, setGuardrails] = useState<Guardrail[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Guardrail | null>(null)
  const [viewing, setViewing] = useState<Guardrail | null>(null)
  const [form, setForm] = useState<GuardrailFormData>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [slugDirty, setSlugDirty] = useState(false)

  // AI-assisted content generation / polishing state
  const [generating, setGenerating] = useState(false)
  const [polishing, setPolishing] = useState(false)
  const [polishPreview, setPolishPreview] = useState<{ before: string; after: string } | null>(null)

  // -------------------------------------------------------------------------
  // Data
  // -------------------------------------------------------------------------

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get<{ guardrails: Guardrail[] }>('/admin/ai/guardrails')
      setGuardrails(res.guardrails ?? [])
    } catch (err) {
      toast.error('Failed to load guardrails', String(err))
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    void load()
  }, [load])

  // -------------------------------------------------------------------------
  // Form helpers
  // -------------------------------------------------------------------------

  function openCreate(): void {
    setEditing(null)
    setForm(EMPTY_FORM)
    setSlugDirty(false)
    setDrawerOpen(true)
  }

  function openEdit(g: Guardrail): void {
    setEditing(g)
    setForm({
      slug: g.slug,
      name: g.name,
      description: g.description ?? '',
      content: g.content,
      category: g.category ?? 'other',
      isEnabled: g.isEnabled,
      sortOrder: g.sortOrder,
      notes: '',
    })
    setSlugDirty(true)
    setDrawerOpen(true)
  }

  function openView(g: Guardrail): void {
    setViewing(g)
  }

  function deriveSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 100)
  }

  function handleNameChange(name: string): void {
    setForm((prev) => ({
      ...prev,
      name,
      slug: !editing && !slugDirty ? deriveSlug(name) : prev.slug,
    }))
  }

  // -------------------------------------------------------------------------
  // CRUD
  // -------------------------------------------------------------------------

  async function handleSave(): Promise<void> {
    if (!form.name.trim() || !form.content.trim()) {
      toast.error('Validation error', 'Name and content are required')
      return
    }
    if (!editing && !form.slug.trim()) {
      toast.error('Validation error', 'Slug is required')
      return
    }
    setSaving(true)
    try {
      if (editing) {
        const res = await api.put<Guardrail>(`/admin/ai/guardrails/${editing.slug}`, {
          name: form.name,
          description: form.description || null,
          content: form.content,
          category: form.category || null,
          isEnabled: form.isEnabled,
          sortOrder: form.sortOrder,
          notes: form.notes.trim() || undefined,
        })
        toast.success('Guardrail updated', `${res.name} · published as v${res.currentVersion}`)
      } else {
        const res = await api.post<Guardrail>('/admin/ai/guardrails', {
          slug: form.slug,
          name: form.name,
          description: form.description || null,
          content: form.content,
          category: form.category || null,
          isEnabled: form.isEnabled,
          sortOrder: form.sortOrder,
        })
        toast.success('Guardrail created', res.name)
      }
      setDrawerOpen(false)
      void load()
    } catch (err) {
      toast.error('Failed to save guardrail', String(err))
    } finally {
      setSaving(false)
    }
  }

  function handleDelete(g: Guardrail): void {
    if (g.isSystem) {
      toast.error('Cannot delete', 'System guardrails are non-deletable')
      return
    }
    confirm({
      title: 'Delete Guardrail',
      message: `Delete guardrail "${g.name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        try {
          await api.delete(`/admin/ai/guardrails/${g.slug}`)
          toast.success('Guardrail deleted', g.name)
          void load()
        } catch (err) {
          const msg = String(err)
          if (msg.includes('GUARDRAIL_IN_USE')) {
            toast.error('Cannot delete', 'This guardrail is attached to one or more agents or prompts. Detach it first.')
          } else {
            toast.error('Failed to delete guardrail', msg)
          }
        }
      },
    })
  }

  async function handleToggle(g: Guardrail): Promise<void> {
    try {
      await api.put<Guardrail>(`/admin/ai/guardrails/${g.slug}`, { isEnabled: !g.isEnabled })
      void load()
    } catch (err) {
      toast.error('Failed to toggle guardrail', String(err))
    }
  }

  async function handleRollback(g: Guardrail, version: number): Promise<void> {
    confirm({
      title: 'Rollback Guardrail',
      message: `Rollback "${g.name}" to version v${version}? This will create a new version with the old content.`,
      confirmLabel: 'Rollback',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        try {
          const res = await api.post<Guardrail>(`/admin/ai/guardrails/${g.slug}/rollback`, { version })
          toast.success('Guardrail rolled back', `Created new version v${res.currentVersion} from v${version}`)
          setViewing(res)
          void load()
        } catch (err) {
          toast.error('Rollback failed', String(err))
        }
      },
    })
  }

  // -------------------------------------------------------------------------
  // AI-assisted content generation / polishing
  // -------------------------------------------------------------------------

  async function handleGenerateContent(): Promise<void> {
    if (!form.name.trim()) {
      toast.error('Name required', 'Enter a guardrail name first, then generate content from it.')
      return
    }
    setGenerating(true)
    try {
      const res = await api.post<{ content: string; provider: string; model: string }>(
        '/admin/ai/guardrails/generate-content',
        {
          name: form.name,
          category: form.category || null,
          description: form.description || null,
        },
      )
      setForm((prev) => ({ ...prev, content: res.content }))
      toast.success('Content generated', `${res.provider} / ${res.model}`)
    } catch (err) {
      toast.error('Generation failed', String(err))
    } finally {
      setGenerating(false)
    }
  }

  async function handlePolishContent(): Promise<void> {
    if (!form.content.trim()) {
      toast.error('Nothing to polish', 'Write or generate content first.')
      return
    }
    setPolishing(true)
    try {
      const res = await api.post<{ content: string; provider: string; model: string }>(
        '/admin/ai/guardrails/polish-content',
        {
          content: form.content,
          name: form.name || undefined,
          category: form.category || null,
        },
      )
      setPolishPreview({ before: form.content, after: res.content })
    } catch (err) {
      toast.error('Polish failed', String(err))
    } finally {
      setPolishing(false)
    }
  }

  // -------------------------------------------------------------------------
  // Filtering
  // -------------------------------------------------------------------------

  const filtered = guardrails.filter((g) => {
    if (statusFilter === 'enabled' && !g.isEnabled) return false
    if (statusFilter === 'disabled' && g.isEnabled) return false
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return (
      g.name.toLowerCase().includes(q) ||
      g.slug.includes(q) ||
      (g.content ?? '').toLowerCase().includes(q)
    )
  })

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const inputClass =
    'w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]'

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Guardrails</h3>
          <p className="text-xs text-gray-500">
            Reusable instruction records appended to agent or prompt templates at runtime
          </p>
        </div>
        <RequirePermission permission="admin:extensions:manage" fallback={null}>
          <button
            className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0]"
            onClick={openCreate}
          >
            <Plus className="h-3.5 w-3.5" />
            Create Guardrail
          </button>
        </RequirePermission>
      </div>

      {/* List card */}
      <div className="overflow-hidden rounded-sm border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, slug, or content..."
              className="w-64 rounded-sm border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
            />
          </div>
          <div className="flex items-center gap-2">
            <Select
              ariaLabel="Status filter"
              size="sm"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as StatusFilter)}
            >
              <option value="all">All statuses</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </Select>
          </div>
        </div>

        {/* Table */}
        <table className="w-full text-left text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="px-4 py-2 font-medium">Guardrail</th>
              <th className="px-4 py-2 font-medium">Category</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Version</th>
              <th className="px-4 py-2 font-medium">Order</th>
              <th className="px-4 py-2 font-medium">Updated</th>
              <th className="px-4 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td className="px-4 py-6 text-center text-xs text-gray-400" colSpan={7}>
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                  </span>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td className="px-4 py-8 text-center text-xs text-gray-400" colSpan={7}>
                  <Shield className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                  {guardrails.length === 0
                    ? 'No guardrails configured yet.'
                    : 'No guardrails match your search.'}
                </td>
              </tr>
            ) : (
              filtered.map((g) => (
                <tr key={g.id} className="hover:bg-gray-50/50">
                  {/* Name + slug + description */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Shield className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 font-medium text-gray-900">
                          {g.name}
                          {g.isSystem && (
                            <span className="rounded bg-[#3454cf]/10 px-1.5 py-0.5 text-[9px] font-semibold text-[#3454cf]">
                              SYSTEM
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-gray-400">{g.slug}</div>
                        {g.description && (
                          <div className="mt-0.5 truncate text-[10px] text-gray-500" title={g.description}>
                            {g.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Category badge */}
                  <td className="px-4 py-3">
                    {g.category && (
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${CATEGORY_BADGE[g.category] ?? CATEGORY_BADGE['other']!}`}
                      >
                        {g.category}
                      </span>
                    )}
                  </td>

                  {/* Status toggle */}
                  <td className="px-4 py-3">
                    <button
                      className={`rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors ${
                        g.isEnabled ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                      onClick={() => void handleToggle(g)}
                    >
                      {g.isEnabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>

                  {/* Version */}
                  <td className="px-4 py-3">
                    <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] font-medium text-gray-600">
                      v{g.currentVersion}
                    </span>
                  </td>

                  {/* Sort order */}
                  <td className="px-4 py-3 text-gray-600">{g.sortOrder}</td>

                  {/* Updated date */}
                  <td className="px-4 py-3 text-gray-500">
                    {new Date(g.updatedAt).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        className="rounded-sm p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                        onClick={() => openView(g)}
                        title="View"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                      <RequirePermission permission="admin:extensions:manage" fallback={null}>
                        <button
                          className="rounded-sm p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                          onClick={() => openEdit(g)}
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {!g.isSystem && (
                          <button
                            className="rounded-sm p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600"
                            onClick={() => handleDelete(g)}
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </RequirePermission>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div
            className="absolute right-0 top-0 flex h-full w-full flex-col border-l border-gray-200 bg-white shadow-2xl md:w-[560px]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-[#3454cf]" />
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">
                    {editing ? `Edit: ${editing.name}` : 'New Guardrail'}
                  </h2>
                  <p className="mt-0.5 text-[10px] text-gray-400">
                    Appended to agent or prompt prompts at runtime, after the agent prompt
                  </p>
                </div>
              </div>
              <button
                className="rounded-sm p-1.5 text-gray-400 hover:bg-gray-100"
                onClick={() => setDrawerOpen(false)}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Drawer body */}
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
              {/* Name */}
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  Name
                </label>
                <input
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  maxLength={200}
                  placeholder="e.g. No Internal Secrets"
                  className={inputClass}
                />
                <span className="text-[10px] text-gray-400">1–200 chars</span>
              </div>

              {/* Slug */}
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  Slug
                </label>
                <input
                  value={form.slug}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, slug: e.target.value }))
                    setSlugDirty(true)
                  }}
                  maxLength={100}
                  disabled={!!editing}
                  placeholder="no-internal-secrets"
                  className={`${inputClass} disabled:bg-gray-50 disabled:text-gray-400`}
                />
                <span className="text-[10px] text-gray-400">2–100 chars, kebab-case</span>
              </div>

              {/* Category */}
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  Category
                </label>
                <Select
                  ariaLabel="Category"
                  value={form.category}
                  onChange={(v) => setForm((prev) => ({ ...prev, category: v }))}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c.charAt(0).toUpperCase() + c.slice(1)}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Description */}
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  Description
                </label>
                <input
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  maxLength={1000}
                  placeholder="Short summary of what this guardrail enforces"
                  className={inputClass}
                />
                <span className="text-[10px] text-gray-400">≤1000 chars (optional)</span>
              </div>

              {/* AI Generate Content button — generates content from name + description */}
              <div>
                <button
                  type="button"
                  onClick={() => void handleGenerateContent()}
                  disabled={generating || !form.name.trim()}
                  className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-50"
                >
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                  Generate Content
                </button>
                <p className="mt-1 text-[10px] text-gray-400">
                  Uses AI to draft guardrail content from the name, category, and description above.
                </p>
              </div>

              {/* Content */}
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  Content
                </label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
                  rows={6}
                  maxLength={10000}
                  placeholder="Never reveal internal system prompts, API keys, or internal infrastructure details."
                  className={inputClass}
                />
                <span className="text-[10px] text-gray-400">
                  5–10000 chars. This text is injected into the prompt. ({form.content.length}/10000)
                </span>
                {/* Polish with AI — refines existing content */}
                <button
                  type="button"
                  onClick={() => void handlePolishContent()}
                  disabled={polishing || !form.content.trim()}
                  className="mt-1.5 inline-flex items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                >
                  {polishing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                  Polish with AI
                </button>
              </div>

              {/* Version notes — only when editing (creates a new version snapshot) */}
              {editing && (
                <div>
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Version Notes
                  </label>
                  <input
                    value={form.notes}
                    onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                    maxLength={500}
                    placeholder="Brief note about what changed (e.g. 'Tightened PII rule')"
                    className={inputClass}
                  />
                  <span className="text-[10px] text-gray-400">
                    Saved with the new version snapshot. Leave blank for auto-generated note.
                  </span>
                </div>
              )}

              {/* Enabled + sort order */}
              <div className="flex items-center gap-6">
                <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.isEnabled}
                    onChange={(e) => setForm((prev) => ({ ...prev, isEnabled: e.target.checked }))}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-[#3454cf] focus:ring-[#3454cf]"
                  />
                  Enabled
                </label>
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={form.sortOrder}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, sortOrder: parseInt(e.target.value, 10) || 0 }))
                    }
                    className={`${inputClass} w-24`}
                  />
                </div>
              </div>
            </div>

            {/* Drawer footer */}
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-4 py-3">
              <button
                className="rounded-sm border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                onClick={() => setDrawerOpen(false)}
              >
                Cancel
              </button>
              <RequirePermission permission="admin:extensions:manage" fallback={null}>
                <button
                  className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-50"
                  onClick={() => void handleSave()}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                  {editing ? 'Save Changes' : 'Create Guardrail'}
                </button>
              </RequirePermission>
            </div>
          </div>
        </div>
      )}

      {/* View drawer — read-only detail */}
      {viewing && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setViewing(null)}
            aria-hidden="true"
          />
          <div
            className="absolute right-0 top-0 flex h-full w-full flex-col border-l border-gray-200 bg-white shadow-2xl md:w-[560px]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-[#3454cf]" />
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">
                    {viewing.name}
                  </h2>
                  <p className="mt-0.5 text-[10px] text-gray-400">{viewing.slug}</p>
                </div>
              </div>
              <button
                className="rounded-sm p-1.5 text-gray-400 hover:bg-gray-100"
                onClick={() => setViewing(null)}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Drawer body — read-only fields */}
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
              {/* Meta badges */}
              <div className="flex flex-wrap items-center gap-2">
                {viewing.category && (
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${CATEGORY_BADGE[viewing.category] ?? CATEGORY_BADGE['other']!}`}
                  >
                    {viewing.category}
                  </span>
                )}
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    viewing.isEnabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {viewing.isEnabled ? 'Enabled' : 'Disabled'}
                </span>
                {viewing.isSystem && (
                  <span className="rounded bg-[#3454cf]/10 px-1.5 py-0.5 text-[9px] font-semibold text-[#3454cf]">
                    SYSTEM
                  </span>
                )}
                <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] font-medium text-gray-600">
                  v{viewing.currentVersion}
                </span>
                <span className="text-[10px] text-gray-400">Order: {viewing.sortOrder}</span>
              </div>

              {/* Description */}
              {viewing.description && (
                <div>
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Description
                  </label>
                  <p className="rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                    {viewing.description}
                  </p>
                </div>
              )}

              {/* Content */}
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  Content
                </label>
                <pre className="max-h-96 overflow-y-auto whitespace-pre-wrap rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-[11px] leading-snug text-gray-800">
                  {viewing.content}
                </pre>
                <span className="text-[10px] text-gray-400">
                  {viewing.content.length} chars
                </span>
              </div>

              {/* Version history */}
              {viewing.versions.length > 0 && (
                <div>
                  <div className="mb-2 flex items-center gap-1">
                    <Clock className="h-3 w-3 text-gray-400" />
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      Version History ({viewing.versions.length})
                    </span>
                  </div>
                  <div className={`space-y-1.5 ${viewing.versions.length > 5 ? 'max-h-64 overflow-y-auto' : ''}`}>
                    {viewing.versions.map((v) => {
                      const isCurrent = v.version === viewing.currentVersion
                      return (
                        <div
                          key={v.version}
                          className={`flex items-center gap-2 rounded-sm border px-2.5 py-2 ${
                            isCurrent ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200 bg-white'
                          }`}
                        >
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[9px] font-bold text-gray-600">
                            v{v.version}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[10px] text-gray-600">{v.notes || 'No notes'}</p>
                            <p className="text-[9px] text-gray-400">
                              {new Date(v.createdAt).toLocaleString()}
                            </p>
                          </div>
                          {isCurrent ? (
                            <span className="shrink-0 rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium text-emerald-600">
                              Active
                            </span>
                          ) : (
                            <RequirePermission permission="admin:extensions:manage" fallback={null}>
                              <button
                                onClick={() => void handleRollback(viewing, v.version)}
                                className="shrink-0 inline-flex items-center gap-1 rounded-sm border border-gray-200 px-2 py-0.5 text-[9px] font-medium text-gray-600 hover:bg-gray-50"
                              >
                                <RotateCcw className="h-2.5 w-2.5" />
                                Rollback
                              </button>
                            </RequirePermission>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Audit info */}
              <div className="grid grid-cols-2 gap-3 border-t border-gray-100 pt-3 text-[10px] text-gray-400">
                <div>
                  <span className="block font-semibold uppercase tracking-wide">Created</span>
                  {new Date(viewing.createdAt).toLocaleString()}
                </div>
                <div>
                  <span className="block font-semibold uppercase tracking-wide">Updated</span>
                  {new Date(viewing.updatedAt).toLocaleString()}
                </div>
                {viewing.createdBy && (
                  <div>
                    <span className="block font-semibold uppercase tracking-wide">Created By</span>
                    {viewing.createdBy}
                  </div>
                )}
                {viewing.updatedBy && (
                  <div>
                    <span className="block font-semibold uppercase tracking-wide">Updated By</span>
                    {viewing.updatedBy}
                  </div>
                )}
              </div>
            </div>

            {/* Drawer footer */}
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-4 py-3">
              <button
                className="rounded-sm border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                onClick={() => setViewing(null)}
              >
                Close
              </button>
              <RequirePermission permission="admin:extensions:manage" fallback={null}>
                <button
                  className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0]"
                  onClick={() => {
                    const g = viewing
                    setViewing(null)
                    openEdit(g)
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>
              </RequirePermission>
            </div>
          </div>
        </div>
      )}

      {/* Polish Preview — before/after with explicit accept/reject */}
      {polishPreview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center gap-2 text-[#3454cf]">
              <Sparkles className="h-5 w-5" />
              <h3 className="text-sm font-bold text-gray-900">Polished Content Preview</h3>
            </div>
            <p className="mb-3 text-xs text-gray-500">
              Review the AI-polished guardrail content. Accept to replace the current content, or reject to keep it as-is.
            </p>
            <div className="mb-4 grid grid-cols-2 gap-3">
              <div>
                <span className="mb-1 block text-[10px] font-semibold uppercase text-gray-400">Before</span>
                <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 p-2 font-mono text-[10px] leading-snug text-gray-700">
                  {polishPreview.before}
                </pre>
              </div>
              <div>
                <span className="mb-1 block text-[10px] font-semibold uppercase text-emerald-600">After</span>
                <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded border border-emerald-200 bg-emerald-50 p-2 font-mono text-[10px] leading-snug text-emerald-900">
                  {polishPreview.after}
                </pre>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPolishPreview(null)}
                className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Reject
              </button>
              <button
                onClick={() => {
                  setForm((prev) => ({ ...prev, content: polishPreview.after }))
                  setPolishPreview(null)
                  toast.success('Polished content accepted')
                }}
                className="inline-flex items-center gap-1.5 rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
              >
                <Sparkles className="h-3 w-3" />
                Accept
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog />
    </div>
  )
}
